import React, { useCallback, useContext } from 'react';
import { Box, CircularProgress, Grid, Paper, Typography } from '@mui/material';
import moment from 'moment'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie, getElementAtEvent } from 'react-chartjs-2';
import { useQuery } from 'react-query';
import axiosClient from '../../../../lib/axios';
import DetalheDialog from './DetalheDialog';
import { AuthContext } from '../../../../contexts/AuthContext';
import SelectPeriodoSazonal, { SelectPeriodoVendasValues } from '../../../../components/SelectPeriodoSazonal';
import SelectQuery from '../../../../components/SelectQuery';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function PosicaoAtualEstoque() {

    const { userPerfilComLoja } = useContext(AuthContext);

    const chartRef = React.useRef<any>();

    const [modalDetail, setModalDetail] = React.useState<any>(null);
    const [filter, setFilter] = React.useState({
        loja: userPerfilComLoja?.loja || null,
        periodo_inicio: moment().subtract(1, 'day').subtract(1, 'year'),
        periodo_fim: moment().subtract(1, 'day'),
        sazonalidade_inicio: moment().subtract(1, 'day').subtract(1, 'year'),
        sazonalidade_fim: moment().subtract(1, 'day').subtract(1, 'year').add('3', 'months')
    })

    const { data, isFetching } = useQuery({
        queryKey: ['posicao-atual-estoque', filter],
        queryFn: () => axiosClient.get("/estoque/posicao-atual-estoque", { params: transformFilters() }),
        refetchOnWindowFocus: false,
        enabled: !!filter.loja,
        retry: false
    });

    let previsao = data?.data;

    const totalPrevisao =
        previsao?.nivel_geral_meses.nivel0a15.total +
        previsao?.nivel_geral_meses.nivel15a30.total +
        previsao?.nivel_geral_meses.nivel30a60.total +
        previsao?.nivel_geral_meses.nivel60mais.total

    let asd = [
        ((previsao?.nivel_geral_meses.nivel0a15.total / totalPrevisao) * 100).toFixed(1),
        ((previsao?.nivel_geral_meses.nivel15a30.total / totalPrevisao) * 100).toFixed(1),
        ((previsao?.nivel_geral_meses.nivel30a60.total / totalPrevisao) * 100).toFixed(1),
        ((previsao?.nivel_geral_meses.nivel60mais.total / totalPrevisao * 100)).toFixed(1),
    ];

    let info = {
        datasets: [
            {
                label: 'Qtd. itens (%)',
                data: asd,
                backgroundColor: ['red', '#AC0909', 'yellow', '#005540']
            },
        ],
    }

    // funções manipuladoras
    const handleChangeFilter = useCallback((prop: "loja", value: any) => {
        setFilter(prevState => ({
            ...prevState,
            [prop]: value
        }))
    }, [])

    const transformFilters = useCallback(() => {
        const transformedFilters = {
            loja_id: filter.loja?.id || null,
            periodo_inicio: filter.periodo_inicio.format('YYYY-MM-DD'),
            periodo_fim: filter.periodo_fim.format('YYYY-MM-DD'),
            sazonalidade_inicio: filter.sazonalidade_inicio.format('YYYY-MM-DD'),
            sazonalidade_fim: filter.sazonalidade_fim.format('YYYY-MM-DD'),
        };

        return transformedFilters;
    }, [filter]);
    //

    return (
        <Box component={Paper}>
            <Typography variant='h6' p={2}>Posição atual do estoque</Typography>

            <Box
                paddingX={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexDirection={{ xs: "column-reverse", md: "row" }}
                gap={1}
            >
                <SelectPeriodoSazonal
                    prevValues={{
                        periodo_inicio: filter.periodo_inicio,
                        periodo_fim: filter.periodo_fim,
                        sazonalidade_inicio: filter.sazonalidade_inicio,
                        sazonalidade_fim: filter.sazonalidade_fim
                    }}
                    onChange={(values: SelectPeriodoVendasValues) => {
                        setFilter(prevState => ({
                            ...prevState,
                            periodo_inicio: values.periodo_inicio,
                            periodo_fim: values.periodo_fim,
                            sazonalidade_inicio: values.sazonalidade_inicio,
                            sazonalidade_fim: values.sazonalidade_fim,
                        }))
                    }} 
                    />

                <SelectQuery
                    defaultValue={filter.loja ? [filter.loja] : []}
                    query="pesquisar-lojas"
                    type="field"
                    label="Loja"
                    titleModal="Selecionar Loja"
                    emptyLabel="-"
                    multiple={false}
                    permitirLimpar={false}
                    returnOnlyId={false}
                    autoSelectFirst={userPerfilComLoja?.loja ? false : true}
                    onChange={(values) => {
                        handleChangeFilter("loja", values[0]);
                    }}
                    extraParams={{ somente_vinculadas: Boolean(userPerfilComLoja?.loja) }}
                />

            </Box>
            {
                (!previsao || isFetching) &&
                <Box p={7} display="flex" flexDirection="column" alignItems="center" gap={2}>
                    <CircularProgress />
                    <Typography>Aguarde, analisando itens ..</Typography>
                </Box>
            }

            {
                (previsao && !isFetching) &&
                <>
                    <Grid container p={2}>
                        <Grid m={{ xs: "0 auto", md: 0 }}>  
                            <Pie
                                ref={chartRef}
                                width={200}
                                onClick={(e) => {
                                    const { current: chart } = chartRef;

                                    if (!chart) {
                                        return;
                                    }

                                    const element = getElementAtEvent(chart, e);

                                    if (!element.length) return;

                                    const { index } = element[0];

                                    if (index === 0) {
                                        setModalDetail(previsao.nivel_geral_meses.nivel0a15.itens);
                                    }

                                    if (index === 1) {
                                        setModalDetail(previsao.nivel_geral_meses.nivel15a30.itens);
                                    }

                                    if (index === 2) {
                                        setModalDetail(previsao.nivel_geral_meses.nivel30a60.itens);
                                    }

                                    if (index === 3) {
                                        setModalDetail(previsao.nivel_geral_meses.nivel60mais.itens);
                                    }

                                }}
                                data={info} />
                        </Grid>

                        <Grid p={2} ml={{ xs: 0, md: 4 }} sx={{ textAlign: { xs: "center", md: "start" } }}>
                            <Typography variant='caption'>Qtd. itens analisados</Typography>
                            <Typography variant='h4'>{previsao.total_itens}</Typography>
                            <Typography component="div" variant='caption' mt={2}>Nível do estoque:</Typography>

                            <Grid container gap={4} justifyContent="center">
                                <Grid>
                                    <div style={{ cursor: 'pointer' }} onClick={() => setModalDetail(previsao.nivel_geral_meses.nivel0a15.itens)}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div style={{ width: 13, height: 13, backgroundColor: 'red', marginRight: 8 }}></div>
                                            <Typography variant='caption'>0 a 15 dias</Typography>
                                        </div>
                                        <Typography variant='h6' fontSize={17}>{previsao.nivel_geral_meses.nivel0a15.total}</Typography>
                                    </div>
                                </Grid>
                                <Grid>
                                    <div style={{ cursor: 'pointer' }} onClick={() => setModalDetail(previsao.nivel_geral_meses.nivel15a30.itens)}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div style={{ width: 13, height: 13, backgroundColor: '#AC0909', marginRight: 8 }}></div>
                                            <Typography variant='caption'>16 a 30 dias</Typography>
                                        </div>
                                        <Typography variant='h6' fontSize={17}>{previsao.nivel_geral_meses.nivel15a30.total}</Typography>
                                    </div>
                                </Grid>
                                <Grid>
                                    <div style={{ cursor: 'pointer' }} onClick={() => setModalDetail(previsao.nivel_geral_meses.nivel15a30.itens)}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div style={{ width: 13, height: 13, backgroundColor: 'yellow', marginRight: 8 }}></div>
                                            <Typography variant='caption'>31 a 60 dias</Typography>
                                        </div>
                                        <Typography variant='h6' fontSize={17}>{previsao.nivel_geral_meses.nivel30a60.total}</Typography>
                                    </div>
                                </Grid>
                                <Grid>
                                    <div style={{ cursor: 'pointer' }} onClick={() => setModalDetail(previsao.nivel_geral_meses.nivel60mais.itens)}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div style={{ width: 13, height: 13, backgroundColor: '#005540', marginRight: 8 }}></div>
                                            <Typography variant='caption'>{`> 60 dias`}</Typography>
                                        </div>
                                        <Typography variant='h6' fontSize={17}>{previsao.nivel_geral_meses.nivel60mais.total}</Typography>
                                    </div>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {
                        modalDetail &&
                        <DetalheDialog
                            filters={transformFilters()}
                            items={modalDetail || []}
                            open={modalDetail ? true : false}
                            handleClose={() => setModalDetail(null)}
                        />
                    }
                </>
            }
        </Box>
    )
}

export type TransformFilters = {
    loja_id: any;
    periodo_inicio: string;
    periodo_fim: string;
    sazonalidade_inicio: string;
    sazonalidade_fim: string;
}